// TODO: DANGEROUS - PLEASE REMOVE @ts-nocheck WHEN POSSIBLE
// @ts-nocheck - this file has a number of typescript errors which are out of scope for the coin migration
import React, { useEffect } from 'react';
// eslint-disable-next-line lodash/import-scope
import { assign, set, get, cloneDeep, find, filter } from 'lodash';
import { connect } from 'react-redux';
import Select from 'react-select';

import analytics from '@nerdwallet/analytics';
import { Impression } from '@nerdwallet/analytics/react/impression';

import { Tracking } from '@nerdwallet/nw-client-lib';
import { getMacroKey } from '@nerdwallet/react-config-builder';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';

// Actions

// Local Components
import CLResultsPanel from '../../components/cost-of-living-calculator/city-life/cl-results-panel/cl-results-panel';
import COLMembershipCta from '../../components/cost-of-living-calculator/shared/col-membership-cta/col-membership-cta';
import FullWidthPanel from '../../components/full-width-panel/full-width-panel';
import Results from '../../components/results/results';
import ToolLinkCards from '../../components/tool-link-cards/tool-link-cards';

// Configs and Data
import { useUrlRecordContext } from '../../components/url-record';
import compareFieldsConfig from '../../config/city-life/compare-fields-config';
import howItComparesConfig from '../../config/city-life/how-it-compares-config';
import navBarConfig from '../../config/city-life/nav-bar-config';
import relatedArticlesConfig from '../../config/city-life/related-articles-config';
import toolLinkConfig from '../../config/city-life/tool-link-config';
import {
  getEventType,
  getEventDetails,
  getEventEntityName,
  getCityLifeJoinNerdwallet,
  FORM_NAME,
} from '../../lib/analytics-constants';
import { COMPARE_FIELD_CHANGE } from '../../lib/cl/cl-actions';
import handleNewSlug from '../../lib/cl/entry/handleSlug';

export interface CLResultsProps {
  dispatch: (p: any) => void;
  cityLife: Record<string, any>;
  history: Record<string, any>;
  identity: Record<string, any>;
  location: Record<string, any>;
  params: Record<string, any>;
  validations: Record<string, any>;
  errors: Record<string, any>;
}

// Lazy Load Components
const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-cl-related-articles' */ '../../components/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const ExpandingLinkList = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-expanding-link-list' */ '../../components/currency/expanding-link-list/expanding-link-list'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

// Constants
const { formInputChanged } = Tracking;
const basePathname = '/cost-of-living-calculator/city-life';

function getCityOptions(cities = []) {
  const sortedCities = cities.sort((a, b) => {
    const stateCompare = a.state.localeCompare(b.state);

    if (stateCompare !== 0) {
      return stateCompare;
    }

    return a.name.localeCompare(b.name);
  });

  const options = sortedCities.map((city) => ({
    label: `${city.name}, ${city.state_abbr}`,
    value: city.id,
    clearableValue: false,
    state: city.state,
  }));

  const groupedOptions = cloneDeep(options);
  let currentState = '';
  let offset = 0;

  options.forEach((option, index) => {
    if (option.state !== currentState) {
      currentState = option.state;

      groupedOptions.splice(index + offset, 0, {
        label: `${currentState}`,
        // @ts-ignore - ignore error
        disabled: true,
      });

      offset += 1;
    }
  });

  return groupedOptions;
}

function filterOptions(options, searchTerm) {
  const query = searchTerm.toLowerCase();

  const optionsWithAllStates = filter(options, (option) => {
    if (
      option.disabled ||
      option.state.toLowerCase().includes(query) ||
      option.label.toLowerCase().includes(query)
    ) {
      // return all state labels because we will filter them out later
      // search by state or city or state_abbrev
      return true;
    }
    // no matches
    return false;
  });

  // filter out states that have no cities - 2 disabled options in a row
  return filter(optionsWithAllStates, (option, index) => {
    if (!optionsWithAllStates[index + 1]) {
      // last option so just check if it is a state
      return !option.disabled;
    }

    return !(option.disabled && optionsWithAllStates[index + 1].disabled);
  });
}

const CLResults = (props: CLResultsProps): JSX.Element => {
  const { history, cityLife, dispatch, validations, params, identity, errors } =
    props;

  useEffect(() => {
    handleNewSlug(params, dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);

  const { title, synopsis } = useUrlRecordContext();

  const handleInputChange = (data) => {
    const { cities } = cityLife;

    if (data?.inputs?.city) {
      const citySlug = find(cities, {
        id: data?.inputs?.city,
      }).slug;

      if (data?.inputs) {
        history.push(`${basePathname}/${citySlug}`);
      }
    } else {
      // this is a compareField input change
      dispatch({
        type: COMPARE_FIELD_CHANGE,
        inputs: data?.inputs,
      });
    }
  };

  const onChange = (config, value, key = null) => {
    let modifiedKey = key;

    if (!modifiedKey) {
      modifiedKey = getMacroKey(config, 'value');
    }

    const data = set(
      {
        name: 'cityLife',
      },
      modifiedKey,
      value
    );

    handleInputChange(data);
  };

  const getCityLifeLinks = () => {
    const data = cityLife;
    const cities = data?.cities ?? [];

    return cities
      .sort((a, b) => a?.name.localeCompare(b?.name))
      .map(({ name, slug, state_abbr: stateAbr }) => ({
        href: `${basePathname}/${slug}`,
        text: `${name}, ${stateAbr}`,
      }));
  };

  const data = {
    ...(cityLife || {}),
    validations,
  };
  const { cities } = data;
  const cityItems = getCityOptions(cities);
  const placeholder = 'Select city';

  let citySlug = '';
  if (data?.inputs?.city) {
    citySlug = find(cities, {
      id: data?.inputs?.city,
    }).slug;
  }

  const resultsPanelProps = {
    data,
    errors,
    onChange,
    handleInputChange,
    navBarConfig,
    howItComparesConfig,
    ...props,
    customComponents: {
      cityInput: () => (
        <Select
          clearable={false}
          value={data?.inputs?.city}
          placeholder={placeholder}
          options={cityItems}
          filterOptions={filterOptions}
          onChange={(config) => {
            const value = get(config, 'value');
            formInputChanged({
              formName: 'config-builder',
              fieldName: 'city_change',
              fieldValue: value,
            });

            analytics.track('cost_of_living_city_updated', {
              form_name: 'mrt_city_life_search_city',
              page_number: null,
              total_pages: null,
              is_valid: true, // there is no validation
              was_prefilled: true,
              value_s: value,
              additional_attributes: {
                city_type: 'search_in_city_life',
              },
            });
            onChange(config, value, 'inputs.city');
          }}
        />
      ),
      compareInput: () => (
        <Select
          clearable={false}
          value={data?.inputs?.compareField}
          options={compareFieldsConfig}
          onChange={(config) => {
            const value = get(config, 'value');
            formInputChanged({
              formName: 'config-builder',
              fieldName: 'comparefield_change',
              fieldValue: value,
            });

            analytics.track('cost_of_living_field_updated', {
              form_name: FORM_NAME.CITY_LIFE_COMPARE_FIELD,
              page_number: null,
              total_pages: null,
              is_valid: true, // there is no validation message
              was_prefilled: true,
              value_s: value,
            });

            onChange(config, value, 'inputs.compareField');
          }}
        />
      ),
    },
  };

  const getEvent = getCityLifeJoinNerdwallet();
  const getEventAttributes = getEventDetails(getEvent);

  return (
    <Impression
      eventType="geo_page_impression"
      entityName="mortgages_city_life"
      payload={{
        additional_attributes: {
          geo_granularity: 'region',
          geo_description: citySlug,
        },
      }}
    >
      <Results
        {...assign({}, props, {
          className: 'cl-app',
          data,
          heading: title,
          methodology: (
            <Methodology
              trackingFormName="col-methodology"
              impressionTracking
            />
          ),
          relatedArticles: <RelatedArticles {...relatedArticlesConfig} />,
          membershipCta: (
            <COLMembershipCta
              identity={identity}
              impressionPayload={{
                eventType: getEventType(getEvent),
                entityName: getEventEntityName(getEvent),
                payload: getEventAttributes,
              }}
            />
          ),
          resultsPanel: <CLResultsPanel {...resultsPanelProps} />,
          split: '100%',
          subcopy: synopsis,
          toolLinkCards: (
            <FullWidthPanel className="tool-link-cards" {...toolLinkConfig}>
              <ToolLinkCards
                cards={toolLinkConfig.cards}
                impressionTrackingName={toolLinkConfig.impressionTrackingName}
                data={data}
              />
            </FullWidthPanel>
          ),
          additionalContent: (
            <FullWidthPanel
              header="Explore city life in other big cities"
              className="city-life-links"
            >
              <ExpandingLinkList
                tracking={{
                  formName: 'seemore_citylife_cities_open',
                  fieldName: 'citylife',
                }}
                links={getCityLifeLinks()}
              />
            </FullWidthPanel>
          ),
        })}
      >
        <div />
      </Results>
    </Impression>
  );
};

CLResults.displayName = 'CLResults';

export default connect((state) => state)(CLResults);
